/**
 * 이용 개요 — 사용량 분석의 시작점 (DESIGN §8.9).
 *
 * **고장 개요와 같은 문법에 재는 것만 바꾼다.** 위젯 골격·비교 축·읽는 법
 * 위치를 그대로 두어, 두 화면을 오갈 때 읽는 법을 다시 배우지 않게 한다.
 *
 * 이 화면이 답하는 질문은 "설비가 멀쩡한가"가 아니라 **"실제로 쓰이는가"**다.
 * 분모는 관측 시간(t_obs)이고 t_up 이 아니다 — 고장 안 난 시간을 분모로 쓰면
 * 고장이 많은 설비의 이용률이 올라가 사용량 지표가 고장 지표를 타고 움직인다.
 */

import { useState } from 'react'

import { EntityPicker } from '@/components/EntityPicker'
import { Section } from '@/components/Section'
import { RANK_ENTITIES, type RankEntity } from '@/lib/types'
import { PowerProfile } from './usage/PowerProfile'
import { UsageKpi } from './usage/UsageKpi'
import { UtilizationRanking } from './usage/UtilizationRanking'
import { UtilizationTrend } from './usage/UtilizationTrend'
import './UsagePage.css'

export function UsagePage() {
  // 비교 축은 페이지가 하나만 갖는다 (§8.6.4) — 위젯마다 따로 두면 같은
  // 화면에서 그래프마다 다른 대상을 보게 된다
  const [entity, setEntity] = useState<RankEntity>('kind')
  const axisLabel = RANK_ENTITIES.find((e) => e.key === entity)?.label ?? ''

  return (
    <div className="usage">
      <header className="usage__intro">
        <p className="usage__lead">
          이 화면은 <strong>설비가 실제로 쓰이는지</strong>를 잽니다. 가동률이 「멀쩡한가」라면
          이용률은 「쓰이는가」입니다 — 멀쩡하지만 아무도 안 쓰는 설비와, 자주 쓰여서 고장이
          드러나는 설비는 전혀 다른 이야기입니다.
        </p>
        <div className="usage__axis">
          <span className="usage__axislabel">비교 축</span>
          <EntityPicker value={entity} onChange={setEntity} label="비교 축" />
          <span className="usage__axishint">
            아래 순위가 <strong>{axisLabel}</strong> 기준으로 바뀝니다
          </span>
        </div>
      </header>

      <UsageKpi />

      <Section
        index={1}
        title="먼저, 얼마나 쓰이는가"
        lead="관측된 시간 중 충전에 쓰인 비중이다. 분모는 관측 시간이지 가동 시간이 아니다 — 고장이 많을수록 이용률이 올라가면 안 된다."
        layout="full"
      >
        <UtilizationRanking entity={entity} />
      </Section>

      <Section
        index={2}
        title="그리고, 언제 쓰이는가"
        lead="이용이 낮은 날이 정말 덜 쓰인 날인지, 그날 관측이 얇았던 것인지는 커버리지를 함께 봐야 갈린다."
        layout="full"
      >
        <UtilizationTrend />
      </Section>

      <Section
        index={3}
        title="그리고, 얼마나 세게·얼마나 많이 충전되는가"
        lead="공개 API 에는 전력량이 없다. 환경공단 충전이력(환경부 직접설치분)의 세션 충전량으로 정격 전력별 평균 충전전력과 충전량을 본다. 전압·전류는 어느 원천에도 없다."
        layout="full"
      >
        <PowerProfile />
      </Section>
    </div>
  )
}
